import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { X } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';

export type InquiryFilters = {
  status?: 'pending' | 'resolved';
  sort?: 'asc' | 'desc';
};

type InquiryFiltersProps = {
  filters: InquiryFilters;
  onFiltersChange: (filters: InquiryFilters) => void;
  totalCount: number;
};

export default function InquiryFilters({ filters, onFiltersChange, totalCount }: InquiryFiltersProps) {
  const posthog = usePostHog();

  const handleSortChange = (value: string) => {
    posthog?.capture('filter_applied', { filter_type: 'inquiry_sort', value });
    onFiltersChange({
      ...filters,
      sort: value as 'asc' | 'desc'
    });
  };

  const handleStatusChange = (value: string) => {
    posthog?.capture('filter_applied', { filter_type: 'inquiry_status', value });
    onFiltersChange({
      ...filters,
      status: value === 'all' ? undefined : (value as 'pending' | 'resolved')
    });
  };

  const resetFilters = () => {
    onFiltersChange({});
  };

  const hasActiveFilters = useMemo(() => {
    return !!filters.status || !!filters.sort;
  }, [filters]);

  const getSortValue = () => {
    if (!filters.sort) return '';
    return filters.sort;
  };

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <h3 className='text-lg font-semibold text-gray-900'>My Inquiries</h3>
          <span className='text-sm text-gray-500'>({totalCount} total)</span>
        </div>
        
        {hasActiveFilters && (
          <Button
            variant='outline'
            size='sm'
            onClick={resetFilters}
            trackingEvent='filters_reset'
            trackingProperties={{ location: 'inquiry_filters' }}
          >
            <X className='mr-2 h-4 w-4' />
            Clear Filters
          </Button>
        )}
      </div>

      <div className='flex gap-4'>
        {/* Status Filter */}
        <Select
          value={filters.status || 'all'}
          onValueChange={handleStatusChange}
        >
          <SelectTrigger className='w-[140px]'>
            <SelectValue placeholder='Status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Status</SelectItem>
            <SelectItem value='pending'>Pending</SelectItem>
            <SelectItem value='resolved'>Resolved</SelectItem>
          </SelectContent>
        </Select>

        {/* Sort */}
        <Select
          value={getSortValue()}
          onValueChange={handleSortChange}
        >
          <SelectTrigger className='w-[180px]'>
            <SelectValue placeholder='Sort by' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='desc'>Newest First</SelectItem>
            <SelectItem value='asc'>Oldest First</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}